/* ============================================================
   storage — the API client.

   One place that knows the wire format, so every component can
   speak in files and folders instead of fetch options. Errors
   arrive as ApiError with the server's own words attached: the
   backend already says "this folder is secure" or "not enough
   room on ojee-hp-zorin drive", and rewriting those into
   "request failed" would throw away the only useful part.
   ============================================================ */

const API = process.env.REACT_APP_API || '';

export class ApiError extends Error {
  constructor(status, body) {
    super(body?.error || `Request failed (${status})`);
    this.status = status;
    this.body = body || {};
    this.locked = !!body?.locked;
    this.ownerOnly = !!body?.owner_only;
    this.driveOffline = !!body?.drive_offline;
  }
}

/** Raised when the session has expired; the shell turns this into a re-login. */
export class AuthExpired extends ApiError {}

let onExpired = () => {};
export const setExpiryHandler = (fn) => { onExpired = fn; };

async function request(token, path, { method = 'GET', json, body, headers, signal } = {}) {
  let res;
  try {
    res = await fetch(`${API}/api/nas${path}`, {
      method,
      signal,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(json ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: json ? JSON.stringify(json) : body,
    });
  } catch (e) {
    if (e.name === 'AbortError') throw e;
    // Offline, DNS, TLS, CORS: all indistinguishable from here, and all mean
    // the same thing to the person looking at the screen.
    throw new ApiError(0, { error: 'Cannot reach the storage server' });
  }
  if (res.status === 204) return null;
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { error: text.slice(0, 200) }; }
  if (!res.ok) {
    if (res.status === 401) { onExpired(); throw new AuthExpired(401, data); }
    throw new ApiError(res.status, data);
  }
  return data;
}

export const api = {
  login: (username, password) =>
    request(null, '/auth', { method: 'POST', json: { username, password } }),
  me: (t) => request(t, '/me'),
  quota: (t) => request(t, '/quota'),
  drives: (t) => request(t, '/drives'),
  /** What the server will do quickly, and how fast it would like to be sent to. */
  limits: () => request(null, '/limits'),

  files: (t, drive, path, signal) =>
    request(t, `/files?drive=${encodeURIComponent(drive)}&path=${encodeURIComponent(path)}`, { signal }),
  allFiles: (t, drive, signal) =>
    request(t, `/files?drive=${encodeURIComponent(drive)}&all=1`, { signal }),
  folders: (t, drive, path, signal) =>
    request(t, `/folders?drive=${encodeURIComponent(drive)}&path=${encodeURIComponent(path)}`, { signal }),

  createFolder: (t, drive, parent_path, name, secure = false) =>
    request(t, '/folders', { method: 'POST', json: { drive, parent_path, name, secure } }),
  renameFolder: (t, id, name) =>
    request(t, `/folders/${id}/rename`, { method: 'POST', json: { name } }),
  setFolderSecure: (t, id, secure) =>
    request(t, `/folders/${id}/secure`, { method: 'POST', json: { secure } }),
  deleteFolder: (t, id, permanent = false) => request(t, `/folders/${id}${permanent ? '?permanent=1' : ''}`, { method: 'DELETE' }),
  moveFolder: (t, id, parent_path) =>
    request(t, `/folders/${id}/move`, { method: 'POST', json: { parent_path } }),

  renameFile: (t, id, name) =>
    request(t, `/files/${id}/rename`, { method: 'POST', json: { name } }),
  moveFile: (t, id, path, drive) =>
    request(t, `/files/${id}/move`, { method: 'POST', json: { path, drive } }),
  /** To the trash, unless `permanent`. */
  deleteFile: (t, id, permanent = false) => request(t, `/files/${id}${permanent ? '?permanent=1' : ''}`, { method: 'DELETE' }),

  batchDelete: (t, file_ids, folder_ids, permanent = false) =>
    request(t, '/batch/delete', { method: 'POST', json: { file_ids, folder_ids, permanent } }),

  /* The trash (Trash.js). Deleting moves things here; these put them back or
     finish the job. */
  trash: (t, signal) => request(t, '/trash', { signal }),
  /** `which`: {ids: [...]} or {batch} — a batch is one delete, for Undo. */
  trashRestore: (t, which) => request(t, '/trash/restore', { method: 'POST', json: which }),
  trashPurge: (t, ids) => request(t, '/trash/purge', { method: 'POST', json: { ids } }),
  trashEmpty: (t, drive) => request(t, '/trash/empty', { method: 'POST', json: drive ? { drive } : {} }),
  batchMove: (t, file_ids, folder_ids, dest_path, dest_drive) =>
    request(t, '/batch/move', { method: 'POST', json: { file_ids, folder_ids, dest_path, dest_drive } }),
  batchCopy: (t, file_ids, folder_ids, dest_path, dest_drive) =>
    request(t, '/batch/copy', { method: 'POST', json: { file_ids, folder_ids, dest_path, dest_drive } }),

  /** opts: {mode: 'view'|'download', expires_in: seconds|null} — sent only when set. */
  share: (t, kind, id, opts = {}) => request(t, '/share', { method: 'POST', json: { kind, id, ...opts } }),
  unshare: (t, kind, id) => request(t, '/share', { method: 'DELETE', json: { kind, id } }),
  sharesMine: (t) => request(t, '/shares/mine'),
  /** No token: that is the entire point of a public link. */
  publicMeta: (token, signal) => request(null, `/public/${encodeURIComponent(token)}`, { signal }),
  publicDownloadUrl: (path) => `${API}${path}`,

  folderStats: (t, id, signal) => request(t, `/folders/${id}/stats`, { signal }),
  batchStats: (t, file_ids, folder_ids, signal) =>
    request(t, '/batch/stats', { method: 'POST', json: { file_ids, folder_ids }, signal }),

  /** One file's row by id — what a deep link to the viewer starts with. */
  file: (t, id, signal) => request(t, `/files/${id}`, { signal }),

  /* Markers on a video or audio file. Every call answers with the file's
     whole marker list, so the player replaces its copy rather than patching. */
  markers: (t, fileId, signal) => request(t, `/files/${fileId}/markers`, { signal }),
  addMarker: (t, fileId, at, note) =>
    request(t, `/files/${fileId}/markers`, { method: 'POST', json: { at, note } }),
  moveMarker: (t, id, at) => request(t, `/markers/${id}`, { method: 'PATCH', json: { at } }),
  deleteMarker: (t, id) => request(t, `/markers/${id}`, { method: 'DELETE' }),
  addMarkerNote: (t, id, body) => request(t, `/markers/${id}/notes`, { method: 'POST', json: { body } }),
  editMarkerNote: (t, noteId, body) =>
    request(t, `/markers/notes/${noteId}`, { method: 'PATCH', json: { body } }),
  deleteMarkerNote: (t, noteId) => request(t, `/markers/notes/${noteId}`, { method: 'DELETE' }),

  readText: (t, id, signal) => request(t, `/files/${id}/text`, { signal }),
  writeText: (t, id, text) => request(t, `/files/${id}/text`, { method: 'PUT', json: { text } }),
  createTextFile: (t, drive, path, name, text = '') =>
    request(t, '/files/text', { method: 'POST', json: { drive, path, name, text } }),

  /** `resume`: pick up this person's unfinished session for the same file,
   *  if there is one; the reply then carries `received` and `verify`. */
  /** `direct`: ask for the drive agent's route; `cont`: carry on this session the usual way. */
  uploadStart: (t, name, size, path, drive, resume = false, direct = false, cont = null) =>
    request(t, '/files', { method: 'POST', json: { name, size, path, drive, resume, direct, ...(cont ? { continue: cont } : {}) } }),
  /** A link to one file on its drive's agent (direct.js); ?inline for the player. */
  fileDirect: (t, id, inline = false) => request(t, `/files/${id}/direct${inline ? '?inline=1' : ''}`),
  adminPerms: (t) => request(t, '/admin/perms'),
  setPerm: (t, drive, userId, perm, value) =>
    request(t, '/admin/perms', { method: 'PUT', json: { drive, user_id: userId || null, perm, value } }),
  /** Prove the file here is the one the session holds the start of. */
  uploadVerify: (t, uid, hashes) => request(t, `/files/${uid}/verify`, { method: 'POST', json: { hashes } }),
  /** Stored files with these sizes (and their hashes), for the duplicate check. */
  dupCheck: (t, sizes) => request(t, '/files/dupcheck', { method: 'POST', json: { sizes } }),
  uploadChunk: (t, uid, offset, blob, signal) =>
    request(t, `/files/${uid}/chunk`, {
      method: 'PUT', body: blob, signal, headers: { 'X-Offset': String(offset) },
    }),
  /** `receipt`: the drive agent's signed hash, for an upload sent to it directly. */
  uploadCommit: (t, uid, receipt) =>
    request(t, `/files/${uid}/commit`, { method: 'POST', ...(receipt ? { json: { receipt } } : {}) }),
  uploadCancel: (t, uid) => request(t, `/files/${uid}`, { method: 'DELETE' }),
  myUploads: (t) => request(t, '/uploads/mine'),
  /** Every upload in flight. Admins get everyone's; everyone else gets theirs. */
  activeUploads: (t, signal) => request(t, '/uploads/active', { signal }),
  /** What this browser still has waiting to upload, so an admin can see it. */
  reportQueue: (t, body) => request(t, '/uploads/queue', { method: 'POST', json: body }),
  cancelUpload: (t, id) => request(t, `/uploads/${id}/cancel`, { method: 'POST' }),
  clearMyUploads: (t) => request(t, '/uploads/mine', { method: 'DELETE' }),

  users: (t) => request(t, '/admin/users'),
  createUser: (t, username, password, role) =>
    request(t, '/admin/users', { method: 'POST', json: { username, password, role } }),
  updateUser: (t, id, patch) =>
    request(t, `/admin/users/${id}`, { method: 'PATCH', json: patch }),
  deleteUser: (t, id) => request(t, `/admin/users/${id}`, { method: 'DELETE' }),
  audit: (t, limit = 60) => request(t, `/admin/audit?limit=${limit}`),

  /** A plain URL the browser can download by itself, for files too large to
   *  stream through memory. The token rides in the query string because a
   *  navigation cannot carry an Authorization header. */
  directUrl: (t, id) => `${API}/api/nas/dl/${id}?t=${encodeURIComponent(t)}`,
  streamUrl: (id) => `${API}/api/nas/files/${id}/download`,

  /** Same bytes, rendered by the browser instead of saved — what the preview
   *  pane points <img>/<video>/<iframe> at. The server decides which types it
   *  is willing to serve inline; anything else comes back as a download.
   *  `v` is only for the browser's cache: a file whose bytes were replaced
   *  (a trim) keeps its id, and must not keep playing the old bytes. */
  inlineUrl: (t, id, v) => `${API}/api/nas/dl/${id}?t=${encodeURIComponent(t)}&inline=1${v ? `&v=${String(v).slice(0, 12)}` : ''}`,
  /** A video's streaming copies: which exist, or how far along they are. */
  fileStreams: (t, id) => request(t, `/files/${id}/streams`),
  /** One streaming copy, for a <video> element (token in the query, like /dl). */
  streamCopyUrl: (t, id, q) => `${API}/api/nas/dl/${id}/s/${q}?t=${encodeURIComponent(t)}`,
  /** View-only links: status and copies, no token. `path` comes from the server. */
  publicJson: (path) => request(null, path.replace(/^\/api\/nas/, '')),

  /* Trimming (Trim.js). A trim is a job on the server: it runs, its result
     waits in a cache, and then it is saved as a copy, swapped in for the
     original, or thrown away. */
  /** Length, frame rate and keyframes of a video, and what you may do with a result. */
  trimInfo: (t, id, signal) => request(t, `/files/${id}/trim`, { signal }),
  trimCreate: (t, id, mode, segments) =>
    request(t, `/files/${id}/trim`, { method: 'POST', json: { mode, segments } }),
  trimJobs: (t, signal) => request(t, '/trim/jobs', { signal }),
  /** Cancel, discard a result, or give up an undo copy — whichever applies. */
  trimDiscard: (t, jid) => request(t, `/trim/${jid}`, { method: 'DELETE' }),
  trimRetry: (t, jid) => request(t, `/trim/${jid}/retry`, { method: 'POST' }),
  trimSave: (t, jid, body) => request(t, `/trim/${jid}/save`, { method: 'POST', json: body }),
  trimReplace: (t, jid) => request(t, `/trim/${jid}/replace`, { method: 'POST' }),
  trimUndo: (t, jid) => request(t, `/trim/${jid}/undo`, { method: 'POST' }),
  /** A result, for a <video> element or a download (token in the query, like /dl). */
  trimResultUrl: (t, jid, download = false) =>
    `${API}/api/nas/dl/trim/${jid}?t=${encodeURIComponent(t)}${download ? '&download=1' : ''}`,
  adminTrim: (t) => request(t, '/admin/trim'),
  adminStreams: (t) => request(t, '/admin/streams'),
};

export { API };
